import React, { useState } from 'react';
import { X, Copy, Check, Link2 } from 'lucide-react';
import { 
  FaWhatsapp, FaFacebook, FaTwitter, FaInstagram, FaTelegram 
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import { copyToClipboard } from '../utils/clipboard';
import api from '../utils/api';

const SHARE_PLATFORMS = [
  { 
    id: 'whatsapp', 
    name: 'WhatsApp', 
    icon: FaWhatsapp, 
    color: '#25D366',
    gradient: 'from-green-500 to-green-600',
    getUrl: (url, title) => `https://wa.me/?text=${encodeURIComponent(`${title}\n${url}`)}`
  },
  { 
    id: 'facebook', 
    name: 'Facebook', 
    icon: FaFacebook, 
    color: '#1877F2',
    gradient: 'from-blue-600 to-blue-700',
    getUrl: (url) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`
  },
  { 
    id: 'twitter', 
    name: 'Twitter', 
    icon: FaTwitter, 
    color: '#1DA1F2',
    gradient: 'from-sky-500 to-sky-600',
    getUrl: (url, title) => `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`
  },
  { 
    id: 'telegram', 
    name: 'Telegram', 
    icon: FaTelegram, 
    color: '#0088cc',
    gradient: 'from-cyan-500 to-cyan-600',
    getUrl: (url, title) => `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`
  },
  { 
    id: 'instagram', 
    name: 'Instagram', 
    icon: FaInstagram, 
    color: '#E4405F',
    gradient: 'from-pink-500 via-purple-500 to-orange-400',
    // Instagram doesn't have direct share URL - copy link instead
    getUrl: null
  },
];

const SharePopup = ({ isOpen, onClose, shareUrl, title, description, itemType, itemId }) => {
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  // Track share event
  const trackShare = async (platform) => {
    try {
      if (itemType && itemId) {
        await api.post('/share-analytics/track', {
          item_type: itemType,
          item_id: itemId,
          platform: platform
        });
      }
    } catch (err) {
      // Silently fail - don't block share action
      console.log('Share tracking failed:', err);
    }
  };

  const handleShare = async (platform) => {
    // Track the share
    await trackShare(platform.id);
    
    if (platform.id === 'instagram') {
      // Instagram doesn't support direct URL sharing - copy link
      handleCopyLink();
      toast.success('Link copied! Paste it on Instagram');
      return;
    }
    
    const url = platform.getUrl(shareUrl, title);
    window.open(url, '_blank', 'width=600,height=400');
  };

  const handleCopyLink = async () => {
    // Track copy action
    await trackShare('copy');
    
    const success = await copyToClipboard(shareUrl);
    if (success) {
      setCopied(true);
      toast.success('Link copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
      data-testid="share-popup-overlay"
    >
      <div 
        className="bg-[#1a1a2e] rounded-3xl p-6 w-full max-w-md relative overflow-hidden"
        onClick={e => e.stopPropagation()}
        data-testid="share-popup"
      >
        {/* Background Glow */}
        <div className="absolute -top-20 -right-20 w-40 h-40 bg-purple-500/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-cyan-500/20 rounded-full blur-3xl" />
        
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          data-testid="share-popup-close"
        >
          <X className="w-5 h-5 text-white" />
        </button>

        {/* Header */}
        <div className="text-center mb-6 relative z-10">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
            <Link2 className="w-8 h-8 text-white" />
          </div>
          <h3 className="text-2xl font-bold text-white mb-2">Share</h3>
          <p className="text-gray-400 text-sm line-clamp-2">{title}</p>
        </div>

        {/* Social Share Buttons */}
        <div className="grid grid-cols-5 gap-3 mb-6 relative z-10">
          {SHARE_PLATFORMS.map((platform) => {
            const Icon = platform.icon;
            return (
              <button
                key={platform.id}
                onClick={() => handleShare(platform)}
                className="flex flex-col items-center gap-2 group"
                data-testid={`share-${platform.id}`}
              >
                <div 
                  className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${platform.gradient} flex items-center justify-center transform group-hover:scale-110 transition-all duration-300 shadow-lg`}
                  style={{ 
                    boxShadow: `0 8px 25px ${platform.color}40`
                  }}
                >
                  <Icon className="w-7 h-7 text-white" />
                </div>
                <span className="text-xs text-gray-400 group-hover:text-white transition-colors">
                  {platform.name}
                </span>
              </button>
            );
          })}
        </div>

        {/* Copy Link Section */}
        <div className="relative z-10">
          <p className="text-xs text-gray-500 mb-2 text-center">या लिंक कॉपी करें</p>
          <div className="flex gap-2">
            <div className="flex-1 bg-white/5 rounded-xl px-4 py-3 border border-white/10">
              <p className="text-sm text-gray-300 truncate">{shareUrl}</p>
            </div>
            <button
              onClick={handleCopyLink}
              className={`px-4 rounded-xl font-medium transition-all ${
                copied 
                  ? 'bg-green-500 text-white' 
                  : 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:opacity-90'
              }`}
              data-testid="share-copy-link"
            >
              {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Powered By */}
        <p className="text-center text-xs text-gray-600 mt-4 relative z-10">
          ANANT Web Portal
        </p>
      </div>
    </div>
  );
};

export default SharePopup;
